import { Component, DestroyRef, inject } from '@angular/core';

import { AsyncPipe } from '@angular/common';
import { ModalComponent } from '@shared/ui/modal/modal.component';
import { ModalHeaderComponent } from '@shared/ui/modal/components/modal-header.component';
import { InputBuscaComponent } from '@shared/components/input-busca/input-busca.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { LogoEmpresaComponent } from '@shared/components/logo-empresa/logo-empresa.component';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { TenantService } from '@shared/services/tenant.service';
import { UsuariosService } from '@modules/usuarios/services/usuarios.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalTenentService } from '../../services/modal-tenent.service';

@Component({
  selector: 'app-modal-tenant-logado',
  imports: [ModalComponent, ModalHeaderComponent, InputBuscaComponent, IconComponent, LogoEmpresaComponent, AsyncPipe],
  templateUrl: './modal-tenant-logado.component.html',
  styleUrl: './modal-tenant-logado.component.scss',
})
export class ModalTenantLogadoComponent {
  private readonly _destroyRef = inject(DestroyRef);
  protected readonly tenantService = inject(TenantService);
  protected readonly modalTenentService = inject(ModalTenentService);
  protected tenantsLoading$ = new BehaviorSubject<boolean>(false);
  protected errorMessage$ = new BehaviorSubject<string | null>(null);

  protected readonly usuarioService = inject(UsuariosService);
  protected usuarioLogado$ = this.usuarioService.usuarioLogado$.pipe(shareReplay(1));

  protected tenants$ = this.modalTenentService._refreshTenants$.pipe(
    switchMap(() => {
      if (!this.modalTenentService.mostrarModal()) {
        return of([]);
      }
      this.tenantsLoading$.next(true);
      return this.usuarioService.listarTenantsUsuario().pipe(
        catchError((error) => {
          this.errorMessage$.next('Erro ao carregar tenants');
          console.error('Erro ao listar tenants:', error);
          return of([]);
        }),
        map((tenants) => {
          this.tenantsLoading$.next(false);
          return tenants;
        }),
      );
    }),
    shareReplay(1),
  );

  protected tenantsFiltrados$ = this.tenants$.pipe(
    switchMap((tenants) => {
      return this.modalTenentService._searchTerm$.pipe(
        map((term) => {
          if (!term) return tenants;
          const termLower = term.toLowerCase().trim();
          return tenants.filter((tenant) => tenant.nome.toLowerCase().includes(termLower));
        }),
      );
    }),
    map((tenants) => {
      return tenants.sort((a, b) => {
        const usuarioTenant = this.usuarioService.usuarioLogado?.tenant;

        if (a.id === usuarioTenant) return -1;
        if (b.id === usuarioTenant) return 1;

        return a.nome.localeCompare(b.nome);
      });
    }),
    shareReplay(1),
  );

  toggleModal(): void {
    this.modalTenentService.toggleModal();
  }

  filtrarTenants(termo: string): void {
    this.modalTenentService._searchTerm$.next(termo);
  }

  isTenantAtual(tenantNome: string): Observable<boolean> {
    return this.usuarioLogado$.pipe(map((usuario) => usuario?.tenant === tenantNome));
  }

  selecionarTenant(tenantId: string): void {
    this.tenantService
      .switchTenant(tenantId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.toggleModal();
          setTimeout(() => {
            window.location.reload();
          }, 300);
        },
        error: (err) => {
          console.error('Erro ao selecionar tenant:', err);
        },
      });
  }
}
